import {
    standardizedLabelCreationSchema,
    StandardizedLabelCreationSchemaType,
    StandardizedLabelIdType,
    standardizedLabelUpdateSchema,
    StandardizedLabelUpdateSchemaType,
    recommendationsFiltersSchema,
    RecommendationsFiltersType,
} from '../../../schemas/standardizedLabel.schemas';
import { baseResponseSchema, BaseResponseSchemaType } from '../../../schemas/repository.schemas';
import HttpReq from '../../../service/httpReq';
import { PathFactory } from '../utils';
import {
    StandardizedLabelListResponseSchemaType,
    standardizedLabelResponseSchema,
    StandardizedLabelResponseSchemaType,
    standardizedLabelListResponseSchema,
    standardizedLabelRelationsResponseSchema,
    StandardizedLabelRelationsResponseSchemaType,
} from './schemas';

class StandardizedLabelsRepo {
    static managementPath = 'api/mgmt/standardized-labels';
    static publicPath = 'api/standardized-labels';
    static cache: Record<string, any> = {};
    private static LIST_STANDARDIZED_LABELS_CACHE_KEY = 'LIST_STANDARDIZED_LABELS';
    private static RELATIONS_CACHE_KEY = 'STANDARDIZED_LABELS_RELATIONS';

    private static CLEAR_CACHE () {
        this.cache = {}
    }

    static listStandardizedLabels(
        successConsumer: (response: StandardizedLabelListResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void,
    ) {
        if (StandardizedLabelsRepo.cache[this.LIST_STANDARDIZED_LABELS_CACHE_KEY]) {
            successConsumer(StandardizedLabelsRepo.cache[this.LIST_STANDARDIZED_LABELS_CACHE_KEY]);
        } else {
            HttpReq.get(
                StandardizedLabelsRepo.publicPath,
                (response: unknown) => {
                    const validated = standardizedLabelListResponseSchema.parse(response);
                    successConsumer(validated);
                    StandardizedLabelsRepo.cache[this.LIST_STANDARDIZED_LABELS_CACHE_KEY] = validated;
                },
                errorConsumer,
            );
        }
    }

    static search(
        searchTerm: string,
        successConsumer: (response: StandardizedLabelListResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void,
    ) {
        const searchParams = new URLSearchParams({
            searchTerm,
        });

        HttpReq.get(
            PathFactory.create([StandardizedLabelsRepo.managementPath], searchParams.toString()),
            (response: unknown) => {
                successConsumer(standardizedLabelListResponseSchema.parse(response));
            },
            errorConsumer,
        );
    }

    static recommendations(
        filters: RecommendationsFiltersType,
        successConsumer: (response: StandardizedLabelListResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void,
    ) {
        recommendationsFiltersSchema.parse(filters);
        const searchParams = new URLSearchParams(filters);

        HttpReq.get(
            PathFactory.create([StandardizedLabelsRepo.managementPath, 'recommendations'], searchParams.toString()),
            (response: unknown) => {
                successConsumer(standardizedLabelListResponseSchema.parse(response));
            },
            errorConsumer,
        );
    }

    static getStandardizedLabel(
        standardizedLabelId: StandardizedLabelIdType,
        successConsumer: (response: StandardizedLabelResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void,
    ) {
        HttpReq.get(
            PathFactory.create([StandardizedLabelsRepo.publicPath, standardizedLabelId]),
            (response: unknown) => {
                successConsumer(standardizedLabelResponseSchema.parse(response));
            },
            errorConsumer,
        );
    }

    static createStandardizedLabel(
        createStandardizedLabelSpec: StandardizedLabelCreationSchemaType,
        successConsumer: (response: StandardizedLabelResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void,
    ) {
        standardizedLabelCreationSchema.parse(createStandardizedLabelSpec);
        HttpReq.post(
            StandardizedLabelsRepo.managementPath,
            createStandardizedLabelSpec,
            (response: unknown) => {
                this.CLEAR_CACHE()
                successConsumer(standardizedLabelResponseSchema.parse(response));
            },
            errorConsumer,
        );
    }

    static updateStandardizedLabel(
        standardizedLabelId: StandardizedLabelIdType,
        updateStandardizedLabelSpec: StandardizedLabelUpdateSchemaType,
        successConsumer: (response: StandardizedLabelResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void,
    ) {
        standardizedLabelUpdateSchema.parse(updateStandardizedLabelSpec);
        HttpReq.put(
            PathFactory.create([StandardizedLabelsRepo.managementPath, standardizedLabelId]),
            { id: standardizedLabelId, ...updateStandardizedLabelSpec },
            (response: unknown) => {
                this.CLEAR_CACHE()
                successConsumer(standardizedLabelResponseSchema.parse(response));
            },
            errorConsumer,
        );
    }

    static getRelations(
        successConsumer: (response: StandardizedLabelRelationsResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void,
    ) {
        if (StandardizedLabelsRepo.cache[this.RELATIONS_CACHE_KEY]) {
            successConsumer(StandardizedLabelsRepo.cache[this.RELATIONS_CACHE_KEY]);
        } else {
            HttpReq.get(
                'api/standardized-label-relations',
                (response: unknown) => {
                    const validated = standardizedLabelRelationsResponseSchema.parse(response);
                    StandardizedLabelsRepo.cache[this.RELATIONS_CACHE_KEY] = validated;
                    successConsumer(validated);
                },
                errorConsumer,
            );
        }
    }

    static deleteStandardizedLabel(
        standardizedLabelId: StandardizedLabelIdType,
        successConsumer: (response: BaseResponseSchemaType) => void,
        errorConsumer?: (...args: any[]) => void,
    ) {
        HttpReq.delete(
            PathFactory.create([StandardizedLabelsRepo.managementPath, standardizedLabelId]),
            (response: unknown) => {
                this.CLEAR_CACHE()
                successConsumer(baseResponseSchema.parse(response));
            },
            errorConsumer,
        );
    }
}

export default StandardizedLabelsRepo;
